import React from "react";
import { Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AssignmentNotification } from "../../../../types/scheduling/meetings";
import { Speaker, TalkMod, WMSchedule } from "../../../../types/scheduling/weekend";
import { CongSettings } from "../../../../types/scheduling/settings";
import { AssignmentSummary } from "../../RecentAssignments";
import { EditWeek } from "../wm_edit_week";
import { IncomingSpeakerInfo } from "./IncomingSpeakerInfo";
import { PrintTalk } from "./PrintTalk";
import { CongName } from "../../common";
import { HGContext } from "../../../../helpers/globals";
import { useContext } from "react";
import { stringToDate, wmDate } from "../../../../helpers/dateHelpers";
import { selectedCong } from "../../../../helpers/langGroups";
import { useWMSpeakers } from "../../../../api/weekend";

interface IncomingSectionProps {
  schedule: WMSchedule;
  langGroupId: number;
  editMode: string;
  setEditMode: (mode: string) => void;
  notifications?: AssignmentNotification[];
  notificationDates: { start: string; end: string };
  absences?: any[];
  events?: any[];
  congSettings: CongSettings;
  userAssignmentMap: Map<number, AssignmentSummary[]>;
  localCongIds: Set<number>;
  onSpeakerInfoClick: (speaker: Speaker) => void;
  refresh: () => void;
}

export const IncomingSection: React.FC<IncomingSectionProps> = (props) => {
  const { t } = useTranslation();
  const ctx = useContext(HGContext);
  const speakersQuery = useWMSpeakers(ctx.globals.cong?.id ?? 0, props.langGroupId, false, {
    enabled: !!props.schedule.speaker2,
  });
  const workingCong = selectedCong(props.langGroupId) || ctx.globals.cong;
  if (!workingCong) return null;

  props.schedule.speaker;
  const speaker2 = speakersQuery.data?.find((s) => s.userId === props.schedule.speaker2);
  const meetingDateStr = wmDate(
    props.schedule.date,
    workingCong,
    props.schedule.event ? [props.schedule.event] : undefined,
  );
  const meetingDate = stringToDate(meetingDateStr);

  const defaultOutput =
    props.schedule.speaker?.id || props.schedule.talk_mod ? (
      <>
        <div className="d-flex flex-column">
          <IncomingSpeakerInfo
            schedule={props.schedule}
            meetingDate={meetingDate}
            absences={props.absences}
            notifications={props.notifications}
            congSettings={props.congSettings}
            userAssignmentMap={props.userAssignmentMap}
            localCongIds={props.localCongIds}
            editMode={props.editMode}
            setEditMode={props.setEditMode}
            onSpeakerInfoClick={props.onSpeakerInfoClick}
            langGroupId={props.langGroupId}
          />
          {!!speaker2 && (
            <IncomingSpeakerInfo
              schedule={props.schedule}
              speaker2={speaker2}
              meetingDate={meetingDate}
              absences={props.absences}
              notifications={props.notifications}
              congSettings={props.congSettings}
              userAssignmentMap={props.userAssignmentMap}
              localCongIds={props.localCongIds}
              editMode={props.editMode}
              setEditMode={props.setEditMode}
              onSpeakerInfoClick={props.onSpeakerInfoClick}
              langGroupId={props.langGroupId}
            />
          )}
        </div>
        <p className="text-muted">
          {props.schedule.speaker.congregation_name
            ? props.schedule.speaker.congregation_name
            : props.schedule.speaker?.id || props.schedule.talk_mod === TalkMod.Other
              ? CongName(props.schedule.speaker?.congregation, ctx, props.congSettings)
              : props.schedule.temp_cong?.id && props.schedule.talk_mod === TalkMod.TBD
                ? CongName(props.schedule.temp_cong, ctx, props.congSettings)
                : props.schedule.talk_mod === TalkMod.Stream
                  ? t("schedules.stream")
                  : ""}
        </p>
        <PrintTalk wmschedule={props.schedule} />
      </>
    ) : (
      <p className="mb-3">{t("schedules.weekend.no-talk-scheduled")}</p>
    );

  let content: React.JSX.Element;
  switch (props.schedule.talk_mod) {
    case TalkMod.TBD:
      if (props.schedule.talk_mod === TalkMod.TBD && !props.schedule.temp_cong) {
        content = (
          <>
            <h4 className="fw-bold me-2">{t("schedules.weekend.tbd")}</h4>
            <PrintTalk wmschedule={props.schedule} />
          </>
        );
      } else {
        content = defaultOutput;
      }
      break;

    case TalkMod.Stream:
      content = (
        <>
          <h4 className="fw-bold me-2">{t("schedules.stream")}</h4>
          <PrintTalk wmschedule={props.schedule} />
        </>
      );
      break;

    default:
      content = defaultOutput;
      break;
  }

  return (
    <Card className="mb-3 p-3">
      <h5 className="border-bottom pb-2 mb-3">{t("schedules.weekend.incoming")}</h5>
      {props.editMode === `week${props.schedule.id}` ? (
        <EditWeek
          schedule={props.schedule}
          langGroupId={props.langGroupId}
          refetchFunc={props.refresh}
          setEditMode={props.setEditMode}
          notifications={props.notifications}
          notificationDates={props.notificationDates}
          absences={props.absences}
          events={props.events}
        />
      ) : (
        <>
          <div className="col-12">{content}</div>
          <div>
            <Button
              variant="outline-primary"
              className="py-2"
              title={t("schedules.weekend.add-speaker")}
              onClick={() => props.setEditMode(`week${props.schedule.id}`)}
            >
              {t("general.edit")}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};
